/**
 * Hooks binding PageSource adapters to react-query.
 *
 * useNumberedList — Clinic lists on the ZOY-285 numbered contract (patients,
 *                   bookings, services, rooms, catalogs, delivered sessions…):
 *                   `page` is requested directly; totals come from the server.
 * useArrayList    — Auth surfaces returning full arrays (members, roles):
 *                   client paging with real totals.
 */

import { useCallback, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { PagedResult, QueryFilter, QuerySort } from './querySpec';
import { arrayView, numberedView, type PageView } from './pageSource';

export interface ListController<T> {
  /** The whole fetched set, before filter and paging — array lists only. */
  all?: readonly T[];
  view: PageView<T>;
  loading: boolean;
  error: unknown;
  perPage: number;
  setPerPage: (n: number) => void;
  goTo: (page: number) => void;
  refetch: () => void;
}

export interface NumberedPageSpec {
  filters: QueryFilter[];
  sort: QuerySort[];
  limit: number;
  /** 1-based (frontend page index 0-based → page = index + 1). */
  page: number;
}

export function useNumberedList<T, R extends PagedResult<T> = PagedResult<T>>(args: {
  keyBase: readonly unknown[];
  fetchPage: (spec: NumberedPageSpec) => Promise<R>;
  filters: QueryFilter[];
  sort: QuerySort[];
  initialPerPage?: number;
}): ListController<T> & { data: R | undefined } {
  const [perPage, setPerPageState] = useState(args.initialPerPage ?? 20);
  const [page, setPage] = useState(1);

  const filterKey = JSON.stringify([args.filters, args.sort, perPage]);

  const query = useQuery({
    queryKey: [...args.keyBase, filterKey, page],
    queryFn: () => args.fetchPage({ filters: args.filters, sort: args.sort, limit: perPage, page }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  // Filter/sort/perPage change returns to page 1.
  const [lastFilterKey, setLastFilterKey] = useState(filterKey);
  if (filterKey !== lastFilterKey) {
    setLastFilterKey(filterKey);
    setPage(1);
  }

  const view = useMemo(() => numberedView<T>(page, query.data), [page, query.data]);

  const goTo = useCallback((target: number) => {
    if (target >= 1) setPage(target);
  }, []);

  const setPerPage = useCallback((n: number) => {
    setPerPageState(n);
    setPage(1);
  }, []);

  return {
    view,
    data: query.data,
    loading: query.isPending,
    error: query.isError ? query.error : null,
    perPage,
    setPerPage,
    goTo,
    refetch: () => void query.refetch(),
  };
}

export function useArrayList<T>(args: {
  keyBase: readonly unknown[];
  fetchAll: () => Promise<T[]>;
  /** Client-side filter applied before paging (search etc.). */
  filter?: (row: T) => boolean;
  staleTime?: number;
  /** How long the list stays in memory once no page shows it (default: React Query's). */
  gcTime?: number;
}): ListController<T> {
  const [perPage, setPerPage] = useState(20);
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: [...args.keyBase],
    queryFn: args.fetchAll,
    staleTime: args.staleTime ?? 60_000,
    ...(args.gcTime !== undefined ? { gcTime: args.gcTime } : {}),
  });

  const filtered = useMemo(() => {
    const all = query.data ?? [];
    return args.filter ? all.filter(args.filter) : all;
  }, [query.data, args.filter]);

  const view = useMemo(() => arrayView(filtered, page, perPage), [filtered, page, perPage]);

  return {
    view,
    all: query.data,
    loading: query.isPending,
    error: query.isError ? query.error : null,
    perPage,
    setPerPage: (n) => {
      setPerPage(n);
      setPage(1);
    },
    goTo: setPage,
    refetch: () => void query.refetch(),
  };
}
